import React from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { Wrapper, Image } from '@farewill/ui'
import { COLOR, GTR } from '@farewill/ui/tokens'
import { screenMin } from '@farewill/ui/helpers/responsive'
import { CLOUDINARY_ROOT_PATH } from '@farewill/ui/components/Image/constants'
import type { FormatPathArgs } from '@farewill/ui/components/Image/types'

import ExternalLink from 'components/ExternalLink'
import TelephoneNumber from 'components/TelephoneNumber'
import { PRODUCTS, PRODUCTS_MAP } from 'lib/products/constants'

type CoBrandedHeaderProps = {
  assetDirectory: string
  partnerLogoUrl: string
  telephoneNumber?: string
  altText: string
}

/**
 * The `FAREWILL_URL` is hardcoded because the `ROOT_URL` config variable may
 * be set to the branded subdomain URL, and we explicitly want to link to the
 * non-cobranded Farewill home page.
 */
const FAREWILL_URL = 'https://farewill.com'

const StyledWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${screenMin.l`
    border-bottom: solid 1px ${COLOR.GREY.LIGHT};
  `}
`

const StyledLockup = styled(Wrapper)`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 18px;

  ${screenMin.m`
    gap: 20px;
  `}
`

const StyledTelephoneNumber = styled(TelephoneNumber)`
  align-self: center;
  display: none;
  margin-right: ${GTR.XXL};

  ${screenMin.m`
    display: block;
  `}
`

/**
 * The header height is set explicitly so the partner logo can have a fixed
 * height rather than `height: 100%`. `<Image>` outputs a 1x/2x `srcset`, and on
 * high density screens Safari halves the intrinsic size of the SVG for the 2x
 * candidate, which made the partner logo's link roughly half the logo's width
 * and caused the Farewill logo to overlap it. A fixed height gives the logo a
 * width (from its aspect ratio) that doesn't depend on its intrinsic size.
 */
const HEADER_HEIGHT = { default: '50px', fromM: '54px' }

const StyledLogoLink = styled(ExternalLink)`
  display: block;
  flex: none;
  height: ${HEADER_HEIGHT.default};

  ${screenMin.m`
    height: ${HEADER_HEIGHT.fromM};
  `}
`

const StyledCoBrandedLogo = styled(Image)`
  display: block;
  width: auto;
  height: ${HEADER_HEIGHT.default};

  ${screenMin.m`
    height: ${HEADER_HEIGHT.fromM};
  `}
`

const StyledFarewillLink = styled(Link)`
  align-content: center;
  box-sizing: border-box;
  flex: none;
  height: ${HEADER_HEIGHT.default};
  padding: 8px 0;

  ${screenMin.m`
    height: ${HEADER_HEIGHT.fromM};
  `}
`

const StyledFarewillLogo = styled(Image)`
  display: block;
`

const CoBrandedHeader = ({
  assetDirectory,
  partnerLogoUrl,
  telephoneNumber,
  altText,
}: CoBrandedHeaderProps): React.ReactElement => (
  <StyledWrapper tag="header" background={COLOR.WHITE}>
    <StyledLockup>
      <StyledLogoLink href={partnerLogoUrl}>
        <StyledCoBrandedLogo
          formatPath={({ path, ext, width }: FormatPathArgs): string =>
            `${CLOUDINARY_ROOT_PATH}/c_scale,f_svg,q_auto,w_${width}/${path}.${ext}`
          }
          path={`cobranding-partner-logos/${assetDirectory}/landing-logo-lockup`}
          ext="svg"
          width={150}
          widthFromM={192}
          alt={altText}
        />
      </StyledLogoLink>

      <StyledFarewillLink to={FAREWILL_URL}>
        <StyledFarewillLogo
          path="internal-logos/logo-farewill"
          width={80}
          widthFromM={90}
          alt="Farewill"
        />
      </StyledFarewillLink>
    </StyledLockup>

    <StyledTelephoneNumber
      telephoneNumber={
        telephoneNumber || PRODUCTS_MAP[PRODUCTS.WILLS].telephoneNumber
      }
    />
  </StyledWrapper>
)

export default CoBrandedHeader
